<template>
    <div>
        <rkm-area
            title="Paciente"
            type="fieldset"
            no-margin-top
            v-if="paciente.nome"
        >

            <rkm-row>
                <rkm-col class="flex-grow-1">
                    <rkm-row>
                        <rkm-col>
                            <h4>
                                {{ paciente.nome }}
                                <strong v-if="paciente.preferenciaNomeId === enums.preferenciaNome.NOME_SOCIAL">
                                    ({{ paciente.nomeSocial }})
                                </strong>

                                <span
                                    v-if="paciente.responsavelFamiliar"
                                    class="ml-1"
                                    title="Responsável familiar"
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="crown" />
                                </span>

                                <span
                                    v-if="isTea"
                                    class="ml-1"
                                    title="Pessoa com Transtorno do Espectro Autista - TEA"
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="infinity" />
                                </span>

                                <span
                                    v-if="isPcd"
                                    class="ml-1"
                                    title="Pessoa com Deficiência - PCD"
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="wheelchair" />
                                </span>
                            </h4>
                        </rkm-col>
                    </rkm-row>

                    <rkm-row v-if="paciente.acompanhamentosSaude && paciente.acompanhamentosSaude.length > 0">
                        <rkm-col>
                            <div class="health-flags">
                                <span class="health-title">
                                    Acompanhamentos:
                                </span>

                                <span
                                    v-for="(acompanhamento, index) in paciente.acompanhamentosSaude"
                                    :key="index"
                                    :class="`health-badge acompanhamento-${acompanhamento.value}`"
                                >
                                    {{ acompanhamento.text }}
                                </span>
                            </div>
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col cols="4">

                            <p>
                                <strong>Prontuário:</strong>
                                {{ paciente.prontuario }}
                            </p>

                            <p>
                                <strong>CPF: </strong>
                                <template v-if="paciente.cpf">
                                    {{ paciente.cpf | formatCpf }}
                                </template>
                                <template v-else>
                                    <i>Não informado</i>
                                </template>
                            </p>

                            <p>
                                <strong>CNS: </strong>
                                <template v-if="paciente.cns">
                                    {{ paciente.cns | formatCns }}
                                </template>

                                <template v-else>
                                    <i>Não informado</i>
                                </template>
                            </p>

                            <p>
                                <strong>RG: </strong>
                                <template v-if="paciente.rg">
                                    {{ paciente.rg }}
                                </template>
                                <template v-else>
                                    <i>Não informado</i>
                                </template>
                            </p>

                            <p>
                                <strong>Telefone: </strong>
                                <template v-if="paciente.telefoneNumero">
                                    {{ paciente.telefoneNumero | formatPhoneOrCellPhone }}
                                </template>
                                <template v-else-if="paciente.telefone">
                                    {{ paciente.telefone | formatPhoneOrCellPhone }}
                                </template>
                                <template v-else>
                                    <i>Não informado</i>
                                </template>
                                <rkm-button
                                    v-if="paciente.telefoneId && verificarPermissao('update-cadastro-paciente')"
                                    @click="$refs.ModalTelefone.open(paciente)"
                                    class="ml-1"
                                    color="primary"
                                    title="Alterar telefone"
                                    v-rkm-tooltip.hover
                                    small
                                >
                                    <rkm-icon icon="pen" />
                                </rkm-button>
                            </p>
                        </rkm-col>

                        <rkm-col cols="4">
                            <p>
                                <strong>Data de nascimento: </strong>
                                <template v-if="paciente.dataNascimento">
                                    {{ paciente.dataNascimento }}
                                </template>
                                <template v-else>
                                    <i>Não informado</i>
                                </template>
                            </p>

                            <p>
                                <strong>Idade: </strong>
                                <template v-if="paciente.idade">
                                    {{ paciente.anos }} anos,
                                    {{ paciente.meses }} meses e
                                    {{ paciente.dias }} dias
                                </template>
                                <template v-else>
                                    <i>Não informado</i>
                                </template>
                            </p>

                            <p>
                                <strong>Sexo: </strong>
                                <template v-if="paciente.sexoId">
                                    {{ paciente.sexoDescricao }}
                                </template>
                                <template v-else>
                                    <i>Não informado</i>
                                </template>
                            </p>

                            <p>
                                <strong>
                                    Nome da mãe:
                                </strong>
                                <template v-if="paciente.nomeMae">
                                    {{ paciente.nomeMae }}
                                </template>
                                <template v-else>
                                    <i>Desconhecido</i>
                                </template>
                            </p>

                            <p>
                                <strong>
                                    Raça/cor:
                                </strong>
                                <template>
                                    {{ paciente.racaCor }}
                                </template>
                            </p>
                        </rkm-col>

                        <rkm-col cols="4">
                            <p>
                                <strong>Classificação: </strong>
                                {{ classificacaoDescricao }}
                            </p>

                            <p>
                                <strong>Unidade de referência: </strong>
                                <template v-if="paciente.unidadeReferencia">
                                    {{ paciente.unidadeReferencia }}
                                </template>
                                <template v-else>
                                    <i>Não informado</i>
                                </template>
                            </p>

                            <p>
                                <strong>Área e Microárea: </strong>
                                {{ paciente.area || 'Não Informada' }} / {{ paciente.microarea || 'Não Informada' }}
                            </p>

                            <p>
                                <strong>INE: </strong>
                                {{ paciente.ine || 'Não Informado' }}
                            </p>
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col>
                            <p>
                                <strong>Endereço: </strong>
                                {{ paciente.endereco || 'Não informado' }}
                            </p>
                        </rkm-col>
                    </rkm-row>
                </rkm-col>
            </rkm-row>
        </rkm-area>

        <ModalTelefone ref="ModalTelefone" />
    </div>
</template>

<script>
// Components.
import ModalTelefone from '@/components/ambulatorio/components/paciente/telefone/Modal'

// Filters.
import format from '@/filters/formats'

//Enuns.
import preferenciaNome from '@/enums/cadastro/cidadao/preferenciaNome'
import classificacao from '@/enums/cadastro/paciente/classificacao'
import subclassificacao from '@/enums/cadastro/paciente/subclassificacao'
import deficienciaBase from '@/enums/cadastro/cidadao/deficienciaBase'
import deficiencia from '@/enums/cadastro/cidadao/deficiencia'

// Mixins.
import { permissao } from '@/mixins'

export default {
    components: { ModalTelefone },
    computed: {
        classificacaoDescricao() {

            if (this.paciente.subclassificacaoId == this.enums.subclassificacao.TEMPORARIO) {
                return 'Temporário'
            }

            if (this.paciente.classificacaoId == this.enums.classificacao.OUTRO_MUNICIPIO) {
                return 'Outro Município'
            }

            return 'Munícipe'
        },
        isTea() {
            const ids = this.paciente.deficienciasBaseIds || []

            return ids.includes(this.enums.deficienciaBase.TEA)
                || this.paciente.deficienciaId === this.enums.deficiencia.AUTISMO
        },
        isPcd() {
            const ids = this.paciente.deficienciasBaseIds || []

            return ids.some(id => id !== this.enums.deficienciaBase.TEA)
                || (!!this.paciente.deficienciaId && this.paciente.deficienciaId !== this.enums.deficiencia.AUTISMO)
        },
    },
    data: () => ({
        enums: {
            preferenciaNome,
            classificacao,
            subclassificacao,
            deficienciaBase,
            deficiencia,
        },
    }),
    filters: {
        ...format,
    },
    mixins: [ permissao ],
    props: {
        paciente: { type: Object, required: true },
    },
}
</script>

<style scoped>
p {
    margin: 0px;
}
.col-svg {
    text-align: center !important;
}
.icon {
    width: auto !important;
    height: 96px !important;
    color: #007bff !important;
}

.health-flags {
    margin-top: 9px;
    padding-top: 2px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    border-top: 1px solid #e5e7eb;
}

.health-title {
    font-size: 12px;
    color: #6b7280;
    font-weight: 600;
}

.health-badge {
    padding: 5px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
}

.acompanhamento-idoso {
    color: #155724;
}

.acompanhamento-gestante {
    color: #ffb6c1;
}

.acompanhamento-hipertenso,
.acompanhamento-diabetico {
    color: #856404;
}

.acompanhamento-saude-mulher {
    color: #b34db2;
}

.acompanhamento-crescimento-desenvolvimento-infantil {
    color: #004085;
}
</style>